import { Form } from "@workspace/web-ui/components/form";
import { cn } from "@workspace/web-ui/lib/utils";
import { FormFieldType } from "../../types";
import FormField from "../form-field/form-field";
import { useTask } from "./hook/use-task";
import { TaskFormType } from "./schema";
import { useMedicationsStore } from "./store/use-medication-store";
import { Loader2 } from "lucide-react";
import { Label } from "@workspace/web-ui/components/label";
import { Input } from "@workspace/web-ui/components/input";
import { Button } from "@workspace/web-ui/components/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@workspace/web-ui/components/select";
import { toast } from "sonner";
import React, { useState } from "react";

interface TaskFormProps {
    type: string
    id: string
    open?: boolean
    onOpenChange: (open: boolean) => void
    className: string
}

// Medication pricing data from PDF
const MEDICATION_PRICING = {
    "Compounded Semaglutide": [
        { dosage: "0.25 mg", price: 199.99 },
        { dosage: "0.5 mg", price: 249.99 },
        { dosage: "0.75 mg", price: 299.99 },
        { dosage: "1 mg", price: 349.99 },
        { dosage: "12.5 mg", price: 550 },
        { dosage: "15 mg", price: 600 },
    ],
    "Compounded Tirzepatide": [
        { dosage: "2.5 mg", price: 249.99 },
        { dosage: "5 mg", price: 299.99 },
        { dosage: "7.5 mg", price: 399.99 },
        { dosage: "10 mg", price: 499.99 },
        { dosage: "12.5 mg", price: 550 },
        { dosage: "15 mg", price: 600 },
    ],
    "Zepbound (Branded Tirzepatide)": [
        { dosage: "2.5 mg", price: 349.99 },
        { dosage: "5 mg", price: 499.99 },
        { dosage: "7.5 mg", price: 599.99 },
        { dosage: "10 mg", price: 699.99 },
        // { dosage: "12.5 mg", price: 550 },
        // { dosage: "15 mg", price: 600 },
    ]
};

export const VITAMIN_OPTIONS = [
    "Vitamin B6",
    "Vitamin B12",
    "L-Carnitine",
];

const MedicationComponent = ({ instructionErrors = [], fieldErrors }: {
    instructionErrors?: string[], fieldErrors?: {
        medication_name: string[];
        dosage: string[];
        cost: string[];
        duration: string[];
        vitamin: string[];
    }
}) => {
    const { medications, updateMedication, removeMedication } = useMedicationsStore();
    const [durationErrors, setDurationErrors] = React.useState<boolean[]>([]);

    // Get available dosages for selected medication
    const getAvailableDosages = (medicationName: string) => {
        return MEDICATION_PRICING[medicationName as keyof typeof MEDICATION_PRICING] || [];
    };

    // Get price for selected medication and dosage
    const getPriceForDosage = (medicationName: string, dosage: string) => {
        const medicationData = MEDICATION_PRICING[medicationName as keyof typeof MEDICATION_PRICING];
        if (!medicationData) return 0;

        const dosageData = medicationData.find(item => item.dosage === dosage);
        return dosageData ? dosageData.price : 0;
    };

    // Handle medication selection
    const handleMedicationChange = (index: number, medicationName: string) => {
        updateMedication(index, 'medication_name', medicationName);
        // Reset dosage and cost when medication changes
        updateMedication(index, 'dosage', '');
        updateMedication(index, 'cost', '');
    };

    // Handle dosage selection
    const handleDosageChange = (index: number, dosage: string) => {
        const medication = medications[index];
        updateMedication(index, 'dosage', dosage);

        // Auto-populate price based on selected medication and dosage
        if (medication?.medication_name) {
            const price = getPriceForDosage(medication.medication_name, dosage);
            updateMedication(index, 'cost', price > 0 ? `$${price.toFixed(2)}` : '');
        }
    };

    return (
        <div className="w-full">
            <div className="space-y-4 mb-4">
                {medications.map((med, index) => (
                    <div key={index} className="flex flex-col bg-white rounded-[16px] p-4">
                        <div className="flex justify-between items-center mb-4">
                            <h1 className="text-lg text-[#2D2F35] font-medium">Medication #{index + 1}</h1>
                            <img
                                src="/icons/trash-icon.svg"
                                alt="Remove medication"
                                onClick={() => removeMedication(index)}
                                className="text-red-500 cursor-pointer h-fit"
                            />
                        </div>
                        <div className="flex gap-2 rounded-lg">
                            <div className="grid grid-cols-2 gap-2 items-center">
                                <div className="flex flex-col gap-2">
                                    <Label htmlFor={`medication_name_${index}`} className="font-medium text-sm">
                                        Medication
                                    </Label>
                                    <Select
                                        value={med.medication_name || ''}
                                        onValueChange={(value) => handleMedicationChange(index, value)}
                                    >
                                        <SelectTrigger id={`medication_name_${index}`} className="button-primary h-fit !font-normal">
                                            <SelectValue placeholder="Select Medication" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Compounded Semaglutide">Compounded Semaglutide</SelectItem>
                                            <SelectItem value="Compounded Tirzepatide">Compounded Tirzepatide</SelectItem>
                                            <SelectItem value="Zepbound (Branded Tirzepatide)">Zepbound (Branded Tirzepatide)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {fieldErrors?.medication_name[index] && (
                                        <p className="text-xs text-red-500">{fieldErrors.medication_name[index]}</p>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2">
                                    <Label htmlFor={`dosage_${index}`} className="text-sm font-medium">
                                        Dosage
                                    </Label>
                                    <Select
                                        value={med.dosage || ''}
                                        onValueChange={(value) => handleDosageChange(index, value)}
                                        disabled={!med.medication_name}
                                    >
                                        <SelectTrigger
                                            id={`dosage_${index}`}
                                            className={`button-primary h-fit !font-normal ${!med.medication_name ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            <SelectValue placeholder={med.medication_name ? "Select Dosage" : "Select medication first"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {med.medication_name && getAvailableDosages(med.medication_name).map((dosageOption) => (
                                                <SelectItem key={dosageOption.dosage} value={dosageOption.dosage}>
                                                    {dosageOption.dosage}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {fieldErrors?.dosage[index] && (
                                        <p className="text-xs text-red-500">{fieldErrors.dosage[index]}</p>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2">
                                    <Label htmlFor={`cost_${index}`} className="text-sm font-medium">
                                        Price
                                    </Label>
                                    <Input
                                        type="text"
                                        id={`cost_${index}`}
                                        value={med.cost || ''}
                                        onChange={(e) => updateMedication(index, 'cost', e.target.value)}
                                        placeholder="$"
                                        className="button-primary h-fit !font-normal"
                                    />
                                    {fieldErrors?.cost[index] && (
                                        <p className="text-xs text-red-500">{fieldErrors.cost[index]}</p>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2 mt-11 sm:mt-7 relative">
                                    <Label htmlFor={`duration_${index}`} className="text-sm font-medium">
                                        Duration
                                    </Label>
                                    <Input
                                        type="text"
                                        id={`duration_${index}`}
                                        value={med.duration || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const isNumber = /^\d*$/.test(value);
                                            updateMedication(index, 'duration', value);
                                            setDurationErrors((prev) => {
                                                const newErrors = [...prev];
                                                newErrors[index] = !isNumber;
                                                return newErrors;
                                            });
                                        }}
                                        placeholder="e.g 3"
                                        className={`button-primary h-fit !font-normal ${durationErrors[index] || fieldErrors?.duration[index] ? 'aria-invalid focus-visible:border-none focus-visible:ring-[#FF3B3B] focus-visible:ring-[2px]' : ''
                                            }`}
                                    />
                                    <p
                                        className="text-[#FF3B3B] text-xs mt-1 duration-warning"
                                        style={{
                                            visibility: (durationErrors[index] || fieldErrors?.duration[index]) ? 'visible' : 'hidden',
                                            minHeight: '1.25em'
                                        }}
                                    >
                                        {durationErrors[index] ? 'Duration must be a number' : fieldErrors?.duration[index] || ''}
                                    </p>
                                    <p className="absolute top-0 mt-10 mr-5 right-0 text-sm text-gray-500">Days</p>
                                </div>
                            </div>
                        </div>

                        {med.medication_name !== "Zepbound (Branded Tirzepatide)" && (
                            <div className="flex flex-col gap-2 mb-5">
                                <Label htmlFor={`vitamin_name_${index}`} className="font-medium text-sm">
                                    Vitamins
                                </Label>
                                <Select
                                    value={med.vitamin || ''}
                                    onValueChange={(value) => updateMedication(index, 'vitamin', value)}
                                >
                                    <SelectTrigger
                                        id={`vitamin_name_${index}`}
                                        className={`button-primary h-fit !font-normal ${fieldErrors?.vitamin[index] ? 'border-red-500 focus:ring-red-500' : ''
                                            }`}
                                    >
                                        <SelectValue placeholder="Select Vitamin" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {VITAMIN_OPTIONS.map((vitamin) => (
                                            <SelectItem key={vitamin} value={vitamin}>
                                                {vitamin}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {fieldErrors?.vitamin[index] && (
                                    <p className="text-xs text-red-500">{fieldErrors.vitamin[index]}</p>
                                )}
                            </div>
                        )}

                       



                        <div className="flex flex-col gap-2 col-span-2">
                            <Label htmlFor={`instructions_${index}`} className="text-sm font-medium">
                                Instructions
                            </Label>
                            <textarea
                                id={`instructions_${index}`}
                                value={med.instructions || ''}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    if (value.length > 200) value = value.slice(0, 200);
                                    updateMedication(index, 'instructions', value);
                                }}
                                placeholder="Instructions"
                                maxLength={200}
                                className="resize-none min-h-[80px] button-primary h-fit !font-normal rounded-lg border border-gray-300 p-2"
                            />
                            <div className="text-xs text-gray-500 text-right">
                                {(med.instructions || '').length}/200
                            </div>
                            {instructionErrors[index] && (
                                <div className="text-xs text-red-500 mt-1">{instructionErrors[index]}</div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* <button
                type="button"
                onClick={addMedication}
                className="text-[#4272DD] w-full border-2 border-[#4272DD] font-semibold flex items-center text-center justify-center gap-2 text-sm sm:text-lg rounded-[8px] h-[44px]"
            >
                <PlusIcon className="h-4 w-4" /> Add new
            </button> */}
        </div>
    );
};

export function TaskForm({ type, id, className, onOpenChange }: TaskFormProps) {
    const medicationsStore = useMedicationsStore();
    const { form, onSubmitToApi, isSubmitting } = useTask({
        id,
        taskType: type
    });
    const [medicationInstructionErrors, setMedicationInstructionErrors] = useState<string[]>([]);
    const [medicationFieldErrors, setMedicationFieldErrors] = useState<{
        medication_name: string[];
        dosage: string[];
        cost: string[];
        duration: string[];
        vitamin: string[];
    }>({
        medication_name: [],
        dosage: [],
        cost: [],
        duration: [],
        vitamin: []
    });

    const handleSubmit = async (data: TaskFormType) => {
        try {
            const formattedMedications = medicationsStore.medications.map(med => {
                if (med.duration) {
                    const num = Number(med.duration);
                    if (!isNaN(num) && med.duration.trim() !== "") {
                        return {
                            ...med,
                            duration: `${num} ${num === 1 ? "day" : "days"}`,
                            vitamin: med.vitamin ? { name: med.vitamin } : undefined,
                        };
                    }
                }
                return {
                    ...med,
                    vitamin: med.vitamin ? { name: med.vitamin } : undefined,
                };
            });



            const formData = {
                ...data,
                type,
                ...(type === "appointment_followup" && { cost: "$129.99" }),
                medications: formattedMedications.length > 0
                    ? formattedMedications
                    : undefined
            };


            // ADD THIS: Validate required medication fields
            if (type === "medication_payment") {
                if (!formData.medications || formData.medications.length === 0) {
                    toast.error('Please add at least one medication', {
                        style: {
                            background: '#DC2626',
                            color: '#FFFFFF',
                            borderColor: '#DC2626',
                            '& svg': {
                                color: '#FFFFFF'
                            }
                        } as React.CSSProperties
                    });
                    return;
                }

                // Reset field errors
                const newFieldErrors = {
                    medication_name: [],
                    dosage: [],
                    cost: [],
                    duration: [],
                    vitamin: []
                };

                let hasError = false;

                // Check each medication for missing required fields
                formData.medications.forEach((med, index) => {
                    if (!med.medication_name) {
                        (newFieldErrors.medication_name as string[])[index] = 'Medication is required';
                        hasError = true;
                    }
                    if (!med.dosage) {
                        (newFieldErrors.dosage as string[])[index] = 'Dosage is required';
                        hasError = true;
                    }
                    if (!med.cost) {
                        (newFieldErrors.cost as string[])[index] = 'Price is required';
                        hasError = true;
                    }
                    if (!med.duration) {
                        (newFieldErrors.duration as string[])[index] = 'Duration is required';
                        hasError = true;
                    }

                    const requiresVitamin = med.medication_name !== "Zepbound (Branded Tirzepatide)";
                    if (requiresVitamin && !med.vitamin) {
                        (newFieldErrors.vitamin as string[])[index] = 'Vitamin is required';
                        hasError = true;
                    }
                });

                setMedicationFieldErrors(newFieldErrors);

                if (hasError) {
                    return;
                }
            }

            // Ensure all medication instructions are at least 5 characters
            let errors: string[] = [];
            if (formData.medications) {
                errors = formData.medications.map((med) => {
                    if (!med.instructions || med.instructions.length < 5) {
                        return 'Instructions must be at least 5 characters.';
                    }
                    return '';
                });
                setMedicationInstructionErrors(errors);
                if (errors.some(Boolean)) {
                    return;
                }
            } else {
                setMedicationInstructionErrors([]);
            }

            await onSubmitToApi(formData);
            form.reset();
            onOpenChange(false)
            toast.success('Task assigned successfully', {
                style: {
                    background: "#6EE7B7",
                    color: "#FFFFFF",
                    borderColor: "#6EE7B7",
                    '& svg': {
                        color: '#FFFFFF'
                    }
                } as React.CSSProperties
            });
            return;
        } catch (error) {
            return toast.error(
                'Failed to add a task.',
                {
                    style: {
                        background: '#DC2626',
                        color: '#FFFFFF',
                        borderColor: '#DC2626',
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                }
            );
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                {type === "appointment_followup" && (
                    <>
                        <FormField
                            name="instructions"
                            label="Instructions"
                            type={FormFieldType.TEXTAREA}
                            placeholder="Instructions"
                            control={form.control}
                            fieldType={FormFieldType.TEXTAREA}
                            className={cn(
                                "w-full button-select !rounded-lg",
                                "!ring-0 !ring-offset-0 h-fit",
                                "focus:!ring-0 focus:!ring-offset-0",
                                "!outline-none focus:!outline-none placeholder:text-gray-400",
                            )}
                        />
                        <FormField
                            name="cost"
                            label="Cost"
                            type={FormFieldType.TEXT}
                            placeholder="$50"
                            control={form.control}
                            fieldType={FormFieldType.TEXT}
                            customRender={({ field }) => (
                                <Input
                                    {...field}
                                    value="$129.99"
                                    readOnly
                                    className={cn(
                                        "w-full button-select !rounded-lg",
                                        "!ring-0 !ring-offset-0 h-fit",
                                        "focus:!ring-0 focus:!ring-offset-0",
                                        "!outline-none focus:!outline-none",
                                    )}
                                />
                            )}
                        />
                    </>
                )}
                {type === "medication_payment" && (
                    <div className={cn("flex flex-col gap-2 w-full bg-white rounded-lg", className)}>
                        <MedicationComponent instructionErrors={medicationInstructionErrors} fieldErrors={medicationFieldErrors} />
                    </div>
                )}
                <Button
                    type="submit"
                    className="button-primary !bg-[#4272DD] h-[44px] rounded-[8px] w-full !text-white flex gap-2 items-center !shadow-none !border-none"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? <Loader2 className="animate-spin duration-200" /> : 'Assign to Patient'}
                </Button>
            </form>
        </Form>
    );
}